import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import CreditsBadge from '../components/CreditsBadge';
import { useRefreshable } from '../context/RefreshContext';
import { useSearchParams, Link } from 'react-router-dom';
import { useT } from '../i18n';
import UserMenu from '../components/UserMenu';
import PublicNav from '../components/PublicNav';
import NavBrand from '../components/NavBrand';
import PageSpinner from '../components/PageSpinner';
import HeaderSearch from '../components/HeaderSearch';
import NotificationBell from '../components/NotificationBell';
import MessagesButton from '../components/MessagesButton';
import { useAuth } from '../context/AuthContext';
import useSeo from '../hooks/useSeo';
import { DESIGNER_STYLES, LIMITS, USER_TYPES } from '../constants/designer';
import { planOf, planOfShop } from '../constants/plans';
import { SHOP_CATEGORIES } from '../constants/shop';
import { fetchDesigners, filterDesigners, locationsOf } from '../firebase/designerService';
import { fetchShops, filterShops, locationsOf as shopLocationsOf } from '../firebase/shopService';
import { fetchProducts, filterProducts } from '../firebase/productService';
import { PRODUCT_TYPES, PRODUCT_COLORS, subtypesOf } from '../constants/productTaxonomy';
import ProductGrid, {
  ProductPager, PRODUCT_PAGE, sortProducts,
} from '../components/ProductGrid';
import useRestoreScroll from '../hooks/useRestoreScroll';
// MyDesigners.css is gone with the tiles that borrowed from it — product
// results are ProductGrid's own now, and nothing here uses an `.md-*` class.
import './SearchPage.css';
import './Dashboard.css';
import BackBar from '../components/BackBar';
import PersonIcon from '../components/PersonIcon';

const EXPERIENCE_STEPS = [1, 3, 5, 10, 15];

// Highest plan first; within a plan, the caller's pre-drawn random key.
//
// COPIES BEFORE SORTING. `filterDesigners` can hand back the cached directory
// array itself when nothing is filtered out, and sorting in place would reorder
// that shared cache for the feed and every other page reading it.
function byTierThenChance(list, tieBreak, resolve = planOf) {
  return [...list].sort((a, b) => {
    const byPlan = resolve(b).searchRank - resolve(a).searchRank;
    if (byPlan) return byPlan;
    return (tieBreak.get(a.uid || a.id) ?? 0) - (tieBreak.get(b.uid || b.id) ?? 0);
  });
}

// ── SEARCH ───────────────────────────────────────────────────────────────────
// Free-text search plus filters over one of the two public directories.
//
// WHAT YOU ARE LOOKING FOR IS THE FIRST CHOICE. A designer and a shop are not
// comparable results — you are either hiring someone or buying something — and
// the filters that narrow them have nothing in common either (styles, years and
// a rate against categories and delivery). So the mode is a switch at the top
// rather than a facet among the others, and the filter row below it changes
// with it. Merging the two into one result list would have meant a filter panel
// where half the controls did nothing to half the results.
//
// STATE LIVES IN THE URL, mode included. Every control writes to the query
// string, so a result set is linkable and survives refresh and the back button
// — none of which is true of component state. It also means the header search
// can hand off simply by navigating.
//
// Filtering is in-memory over a cached directory (see designerService and
// shopService), so every keystroke is instant and costs no reads.
// ── ONE PAGE, THREE ADDRESSES ────────────────────────────────────────────────
// `/search` is the page itself; `/designers` and `/shop` are the same page
// LOCKED to one kind of result, so each is a page about one thing rather than a
// search with a tab pre-selected.
//
// `defaultMode` sets which kind. `locked` drops the three mode tabs with it —
// on /designers there is nothing to switch to, and a tab row whose other two
// options navigate away from the page you just chose is a worse answer than no
// tab row. /search keeps them: it is the general search, and the one place the
// shops directory is reachable from.
//
// Not three copies of a 600-line page: the directory and the marketplace differ
// in which rows they filter and which controls narrow them, and both of those
// already branch on `mode` inside here.
export default function SearchPage({ defaultMode, locked = false }) {
  const { t } = useT();
  const { user } = useAuth();
  // Every control on this page — the mode tabs included — writes here. See
  // setMode for why the tabs no longer change the path.
  const [params, setParams] = useSearchParams();

  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Designers unless the URL says otherwise — the directory that existed first
  // and the one every existing /search link means.
  //
  // `product` is a third answer to the same question. It searches LISTINGS
  // rather than accounts, which is a different collection and a different set
  // of filters, but the same act: deciding what you are looking for before
  // narrowing it.
  const MODE_PRODUCT = 'product';
  const rawMode = params.get('type');
  // PRODUCTS BY DEFAULT, matching the tab order. It used to default to
  // designers while products sat first in the row, so opening /search showed
  // the first tab unselected and the second one highlighted — which reads as
  // the page having ignored the order rather than as a deliberate default.
  //
  // An explicit `?type=` still wins, so every existing link keeps working:
  // /search?type=designer and ?type=shop open exactly where they always did.
  //
  // `defaultMode` is what an absent one means — /designers says designers,
  // /shop says products, /search says products as it always has. When the page
  // is LOCKED the parameter is not consulted at all: /designers?type=product
  // would otherwise render products under a heading and a URL that both say
  // designers.
  const mode = locked ? defaultMode
    : rawMode === USER_TYPES.SHOP ? USER_TYPES.SHOP
      : rawMode === USER_TYPES.DESIGNER ? USER_TYPES.DESIGNER
        : rawMode === MODE_PRODUCT ? MODE_PRODUCT
          : (defaultMode || MODE_PRODUCT);
  const isShopMode = mode === USER_TYPES.SHOP;
  const isProductMode = mode === MODE_PRODUCT;

  // ── WHAT EACH OF THE THREE ROUTES CALLS ITSELF ─────────────────────────────
  // One component, three URLs, so the metadata has to follow `locked` rather
  // than the component. /designers and /shop are the two curated, indexable
  // pages and each says what it lists. /search is the same rows behind a
  // free-text query — Google's own guidance is to keep internal search results
  // out of the index, so it carries noindex,follow: not indexed itself, but
  // the crawler still walks through to the profiles and listings on it.
  //
  // The canonical is the bare path in every case. Filters and queries live in
  // the query string, and each combination would otherwise be a separate URL
  // announcing itself as the original of the same content.
  const seo = !locked
    ? {
      title: 'Search — Tunify',
      description: 'Search interior designers, furniture shops and product listings on Tunify.',
      path: '/search',
      noindex: true,
    }
    : isProductMode
      ? {
        title: 'Furniture & Decor Shop — Tunify',
        description: 'Browse furniture, lighting and decor from local shops, and see each piece in your own 3D room design.',
        path: '/shop',
      }
      : {
        title: 'Find Interior Designers — Tunify',
        description: 'Browse interior designers by style, location and experience, and message them about your home design project.',
        path: '/designers',
      };
  useSeo(seo);

  const q = params.get('q') || '';
  const location = params.get('loc') || '';
  // Per-mode filters. Reading both regardless keeps the hook order fixed; only
  // the active mode's are ever applied or rendered.
  const styles = (params.get('styles') || '').split(',').filter(Boolean);
  const minExperience = params.get('exp') || '';
  const categories = (params.get('cats') || '').split(',').filter(Boolean);
  const delivery = params.get('delivery') === '1';
  // Product mode. `ptype` rather than `type`, which the mode itself owns.
  const pType = params.get('ptype') || '';
  const pSubtype = params.get('sub') || '';
  const pColor = params.get('color') || '';
  const minPrice = params.get('min') || '';
  const maxPrice = params.get('max') || '';
  // Product mode only, and in the URL like everything else here — so a sorted,
  // paged result set is as linkable as a filtered one. Same parameter names as
  // the landing catalogue, which runs the same grid.
  //
  // MIXED BY DEFAULT here, where the landing catalogue still defaults to newest.
  // The two are answering different questions: the catalogue is a shelf you
  // browse, and newest-first is the useful order for one. A search result is a
  // list every shop wants to be at the top of, and 'newest' hands that position
  // to whoever uploaded most recently — a race, not a ranking. A fresh draw per
  // visit gives every free shop the same chance at it, and the paid placement
  // lives in its own section above rather than by colonising this one.
  const pSort = params.get('sort') || 'mix';
  const page = Math.max(1, Number(params.get('p')) || 1);

  // The chips for whichever mode is active — one control, two vocabularies.
  const chips = isShopMode ? categories : styles;
  const chipParam = isShopMode ? 'cats' : 'styles';

  // Open if the URL already carries chip filters — arriving at a shared search
  // and not seeing which ones produced it would be worse than the extra rows.
  // Initialiser, not an effect, so it doesn't slam shut as you deselect the last
  // chip while the panel is open.
  const [stylesOpen, setStylesOpen] = useState(() => chips.length > 0);
  // WHO SELLS EACH LISTING, for the byline on its tile. One extra cached
  // directory read in product mode, and none after that — the same map the
  // landing catalogue builds. It cannot come from OwnerBadge, which is what
  // this page used before: OwnerBadge renders a Link of its own, and a tile is
  // now a link to the product, so that would be an anchor inside an anchor.
  const [shops, setShops] = useState([]);
  useEffect(() => {
    if (!isProductMode) return undefined;
    let live = true;
    fetchShops().then(rows => { if (live) setShops(rows); }).catch(() => {});
    return () => { live = false; };
  }, [isProductMode]);
  const ownerOf = useMemo(() => {
    const m = new Map();
    for (const s of shops) m.set(s.uid || s.id, s);
    return (uid) => m.get(uid);
  }, [shops]);

  // Extracted so pull-to-refresh can re-run it; `alive` is a ref because a
  // refresh can still be in flight when the page unmounts.
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  // Only the ACTIVE directory is fetched. Both are cached at module level, so
  // switching modes back and forth costs one read each and none after that.
  // Only the ACTIVE directory is fetched. All three are cached at module level,
  // so switching modes back and forth costs one read each and none after that.
  //
  // Products re-fetch when the TYPE changes as well as the mode — that filter
  // is served by the query rather than in memory (see productService), because
  // a listing carries its thumbnail inline and the whole catalogue will not fit
  // in a search box.
  const load = useCallback(async ({ force = false } = {}) => {
    try {
      const rows = isProductMode ? await fetchProducts(pType, { force })
        : isShopMode ? await fetchShops({ force })
          : await fetchDesigners({ force });
      if (alive.current) { setAll(rows); setError(''); }
    } catch (err) {
      if (!alive.current) return;
      // A failure is shown as one. It used to come back as an empty list,
      // which read as "nothing matches" and hid every real problem behind a
      // result nobody could tell from a genuine one.
      setAll([]);
      setError(err?.message || 'load failed');
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [isShopMode, isProductMode, pType]);

  useEffect(() => { load(); }, [load]);
  // FORCED. The directory is cached for five minutes (designerService), so a
  // plain re-fetch would hand back the very list already on screen and the
  // pull would appear to do nothing. Asking to refresh is exactly the case the
  // cache should be skipped for.
  //
  // Filtering is client-side off `all`, so re-fetching the list is the whole
  // refresh — the visible results recompute from it.
  useRefreshable(() => load({ force: true }));

  // Writes one param without disturbing the others, and drops empties so the
  // URL stays readable instead of accumulating `&exp=&loc=`.
  //
  // NARROWING THE RESULTS RESTARTS AT PAGE ONE. Someone on page 3 of chairs who
  // picks "beige" is asking a new question, and leaving them on page 3 of a
  // three-result list is a blank screen. Handled here rather than at each of
  // the dozen callers, which is how one of them would end up forgetting.
  const setParam = useCallback((key, value) => {
    const next = new URLSearchParams(params);
    if (value === '' || value == null || (Array.isArray(value) && !value.length)) next.delete(key);
    else next.set(key, Array.isArray(value) ? value.join(',') : value);
    if (key !== 'p') next.delete('p');
    setParams(next, { replace: true });
  }, [params, setParams]);

  // Switching what you are looking for starts a new search. The old filters
  // cannot carry over — "15 years' experience" means nothing to a shop and
  // "delivers" means nothing to a designer — and leaving them in the URL would
  // silently narrow the new list by controls no longer on screen. The QUERY
  // survives, because the words you typed are the one thing that still applies.
  const setMode = useCallback((next) => {
    const p = new URLSearchParams();
    // ALWAYS WRITTEN, including for designers.
    //
    // This used to omit `type` for the designer mode, on the reasoning that
    // designers were the default and an absent parameter already meant them.
    // The default later became PRODUCTS (see `mode` above) and this was not
    // changed with it — so pressing Designers cleared the query string, which
    // now reads as "products", and the tab silently refused to switch. Worse,
    // `setLoading(true)` below had already fired while `load`'s identity did
    // not change, so nothing ever set it back: the page sat on its spinner for
    // good.
    p.set('type', next);
    if (q) p.set('q', q);
    // ── THE SWITCH STAYS ON THE PAGE IT SWITCHES ─────────────────────────────
    // This used to redirect two of the three tabs to the dedicated addresses —
    // Designers to /designers, Products to /shop — on the reasoning that a
    // reader who picks designers should land on the page that is about them.
    //
    // But those two routes are LOCKED, and a locked page renders no tab row at
    // all (see `!locked` below). So pressing either tab navigated to a page
    // where the control had vanished: the reader had asked to switch and been
    // handed a page with nothing to switch with, and no way back to the other
    // two modes short of the browser's Back button. Only Shops — the one mode
    // with no dedicated address — actually worked.
    //
    // So mode is a query parameter here, as it already was for shops, and all
    // three tabs behave alike. /designers and /shop are unaffected: they stay
    // the curated, indexable entry points reached from the homepage and the
    // nav, which is the job they were added for. `replace`, so flipping tabs
    // does not fill the back button.
    setParams(p, { replace: true });
    setStylesOpen(false);
    setLoading(true);
  }, [q, setParams, MODE_PRODUCT]);

  const chipLabel = useCallback(
    (k) => t(isShopMode ? `shop_cat_${k}` : `ds_style_${k}`),
    [t, isShopMode],
  );

  // The chip arrays are rebuilt from the query string on every render, so they
  // can't be dependencies directly — a fresh array each time would defeat the
  // memo. The joined string is the stable equivalent.
  const styleKey = styles.join(',');
  const catKey = categories.join(',');

  // NEVER YOURSELF. Search is for finding someone to work with, and your own
  // card is the one result that can never be useful — you cannot message or
  // hire yourself, and it sits in the list taking a slot. Filtered here rather
  // than in the query: the directory is one cached fetch shared by search, the
  // feed and the profile pages, so it must keep everybody in it.
  //
  // Applied to `all`, not just the results, so the location dropdown and the
  // result count are consistent with the list they describe. Harmless for a
  // customer, whose uid is never in the designer directory anyway.
  const others = useMemo(
    () => (user?.uid ? all.filter(d => (d.uid || d.id) !== user.uid) : all),
    [all, user?.uid],
  );

  // ── PLAN FIRST, THEN CHANCE — BUT STABLE WHILE YOU TYPE ────────────────────
  // Same rule as the recommendations rail: gold designers lead, then silver,
  // bronze, free, with the order inside each tier drawn at random so nobody on
  // the same tier is permanently ahead.
  //
  // The randomness CANNOT be drawn inside the sort here, though. This list
  // recomputes on every keystroke and every filter change, so a fresh shuffle
  // each time would reorder the results under the reader's cursor as they type
  // — which on the rail is invisible and here would be unusable.
  //
  // So each designer gets ONE random number, drawn when the directory loads,
  // and the sort is a deterministic comparator over (tier, that number). The
  // order is random per visit and completely stable within it. It is also a
  // valid comparator — consistent for any given pair — which a comparator
  // returning Math.random() would not be.
  const tieBreak = useMemo(() => {
    const m = new Map();
    for (const d of all) m.set(d.uid || d.id, Math.random());
    return m;
  }, [all]);

  const results = useMemo(
    () => (isProductMode
      // The SAME filter and the SAME sort the landing catalogue runs, so a
      // query cannot match here and miss there, or come back in another order.
      ? sortProducts(filterProducts(
        others, q,
        { type: pType, subtype: pSubtype, color: pColor, minPrice, maxPrice },
        t,
      ), pSort, tieBreak)
      : isShopMode
        ? byTierThenChance(filterShops(
          others, q,
          { categories: catKey ? catKey.split(',') : [], location, delivery },
          chipLabel,
        ), tieBreak, planOfShop)
        : byTierThenChance(filterDesigners(
          others, q,
          { styles: styleKey ? styleKey.split(',') : [], minExperience, location },
          chipLabel,
        ), tieBreak)),
    [isProductMode, isShopMode, others, q, styleKey, catKey, minExperience, delivery,
      location, chipLabel, pType, pSubtype, pColor, minPrice, maxPrice, pSort, t, tieBreak],
  );

  // ── RECOMMENDED: WHAT THE PAID TIERS BUY IN SEARCH ────────────────────────
  // A separate section above the results holding only gold and silver shops'
  // listings, gold first. This is the placement those tiers are sold; the list
  // below stays complete and unweighted, so a free shop's listing is never
  // pushed down the page it was already found on.
  //
  // DRAWN FROM `results`, NOT FROM THE WHOLE CATALOGUE — so it obeys the query,
  // the type, the colour and the price range exactly as the grid below does.
  // Recommending a gold sofa to someone who searched for lamps would be an
  // advert, and this is meant to be a shortcut.
  //
  // The tier comes from the OWNER: a listing has no plan of its own. An owner
  // the shop directory has not resolved yet scores 0 and is simply not
  // recommended — it still appears in full below, so nothing is ever hidden by
  // a lookup that has not landed.
  //
  // Ordered gold → silver, then by the same pre-drawn key the rest of the page
  // uses, so two gold shops trade places between visits but not between
  // keystrokes.
  const REC_MAX = 6;
  const [recOpen, setRecOpen] = useState(false);
  const recommended = useMemo(() => {
    if (!isProductMode) return [];
    const rankOf = (p) => planOfShop(ownerOf(p.ownerUid)).searchRank;
    return results
      .filter(p => rankOf(p) > 0)
      .sort((a, b) => rankOf(b) - rankOf(a)
        || (tieBreak.get(a.id) ?? 0) - (tieBreak.get(b.id) ?? 0));
  }, [isProductMode, results, ownerOf, tieBreak]);

  // Collapsed to six — one or two rows, enough to be a shortcut without
  // becoming the page. The button is only worth drawing when it would reveal
  // something, so a run of exactly six shows no control.
  const recShown = recOpen ? recommended : recommended.slice(0, REC_MAX);

  // Reopening the section on every keystroke would be as jarring as closing it,
  // so this only resets when the reader moves to a different result set
  // entirely — a new mode, a new query.
  useEffect(() => { setRecOpen(false); }, [mode, q]);

  // ── PAGING (products only) ────────────────────────────────────────────────
  // CLAMPED AT READ TIME, not by an effect. A filter that narrows forty
  // results to three leaves `p=4` in the URL, and a page past the end renders
  // an empty grid under a count that says there are three — so the last page
  // is shown instead. Doing it here rather than in a setState avoids the
  // render that would show that empty grid before the correction landed.
  const pageCount = Math.max(1, Math.ceil(results.length / PRODUCT_PAGE));
  const at = Math.min(page, pageCount);
  const visible = isProductMode
    ? results.slice((at - 1) * PRODUCT_PAGE, at * PRODUCT_PAGE)
    : results;

  const gridRef = useRef(null);
  const goPage = (n) => {
    const next = Math.min(Math.max(1, n), pageCount);
    if (next === at) return;
    setParam('p', next === 1 ? '' : String(next));
    // Back to the top OF THE RESULTS, not of the document: page 2 starts at
    // its first tile, and leaving the reader halfway down a fresh grid — or
    // throwing them up past the filter panel — are both wrong.
    gridRef.current?.scrollIntoView({ block: 'start' });
  };

  // Opening a listing is a navigation now, so coming back has to land where it
  // left. Same hook the landing catalogue uses, and for the same reason.
  const rememberScroll = useRestoreScroll('sp_prod_y', !loading);

  // From `others` too — a city that only your own profile is in would sit in
  // the filter offering a search that always comes back empty.
  const locations = useMemo(
    () => (isShopMode ? shopLocationsOf(others) : locationsOf(others)),
    [others, isShopMode],
  );
  const hasFilters = isProductMode
    ? Boolean(q || pType || pSubtype || pColor || minPrice || maxPrice)
    : Boolean(q || location || chips.length || (isShopMode ? delivery : minExperience));

  function toggleChip(key) {
    setParam(chipParam, chips.includes(key) ? chips.filter(s => s !== key) : [...chips, key]);
  }

  return (
    <div className="dash-root sp-root">
      <header className="dash-nav">
        <NavBrand />
        <div className="dash-nav-right">
          <HeaderSearch />
          <NotificationBell />
          <CreditsBadge />
          <MessagesButton />
          {user ? <UserMenu /> : <PublicNav />}
        </div>
      </header>

      {/* ── WHEN THE BACK BAR MAY BE HIDDEN ON A PHONE ──────────────────────
          `hideOnMobile` exists because /search is a TAB on phones, and Back
          means nothing on a top-level destination. Two things make that untrue
          here, and both left a reader with no way off the page:

            · the tab bar only renders for a SIGNED-IN user (see MobileTabBar,
              which returns null without one) — so a visitor browsing signed
              out had neither a tab bar nor a back bar;
            · /designers and /shop are not tabs at all. They are reached from
              the homepage, and Back is the only way back to it.

          So the bar hides only where something else really does the job. */}
      {/* `fallback` is where Back goes when there is no history to go back TO —
          a pasted link, a new tab. /dashboard is right for someone signed in;
          for a visitor it is a protected route that bounces them to /login,
          which is a strange answer to "back" from a public directory. The
          homepage is the honest one. */}
      {/* `width` MUST equal the <main> below it. It was 1240 while `.sp-main`
          is 1400, so on any viewport wider than 1400 the bar centred in a
          narrower column than the content and Back sat 80px INSIDE the page it
          points away from — the further from a phone, the worse. */}
      <BackBar
        width={1400}
        hideOnMobile={!locked && !!user}
        fallback={user ? '/dashboard' : '/'}
      />

      <main className="dash-main sp-main">
        <h1 className="sp-title">
          {isProductMode ? t('search_products')
            : isShopMode ? t('search_shops')
              : t('search_designers')}
        </h1>

        {/* ── WHAT ARE YOU LOOKING FOR ──
            First, above the query box, because it decides what every control
            below it means. A segmented group rather than a dropdown: there are
            three answers and all should be visible without opening anything.

            ABSENT ON THE LOCKED PAGES. /designers and /shop are each about one
            kind of thing; a row whose other two tabs navigate off the page you
            just chose is chrome that only offers to undo the choice. The
            general search at /search keeps it. */}
        {!locked && (
        <div className="sp-modes" role="tablist" aria-label={t('search_mode')}>
          {/* PRODUCTS FIRST. Someone arriving at search usually wants a thing
              they can buy; designers and shops are who provides it, which is
              the follow-up question rather than the opening one. Order here is
              display only — the default mode still comes from the URL, so an
              existing link to ?mode=designer opens where it always did. */}
          {[
            { key: MODE_PRODUCT,        label: t('search_mode_products') },
            { key: USER_TYPES.DESIGNER, label: t('search_mode_designers') },
            { key: USER_TYPES.SHOP,     label: t('search_mode_shops') },
          ].map(m => (
            <button
              key={m.key}
              type="button"
              role="tab"
              aria-selected={mode === m.key}
              className={`sp-mode${mode === m.key ? ' sp-mode--on' : ''}`}
              onClick={() => mode !== m.key && setMode(m.key)}
            >{m.label}</button>
          ))}
        </div>
        )}

        {/* ── search + filters ── */}
        <div className="sp-controls">
          <input
            className="sp-search"
            type="search"
            value={q}
            onChange={e => setParam('q', e.target.value)}
            placeholder={isProductMode ? t('search_product_ph') : isShopMode ? t('search_shop_ph') : t('search_ph')}
            aria-label={isProductMode ? t('search_product_ph') : isShopMode ? t('search_shop_ph') : t('search_ph')}
          />

          {/* ── PRODUCT FILTERS ──────────────────────────────────────────────
              Type, subtype, colour and a price range — a different set from the
              account modes, so a different row rather than a branch inside
              theirs. Type comes first because it is the only one served by the
              query (see productService); the rest refine what it returns.

              Changing the type clears the subtype for the same reason it does
              in the publish dialog: a subtype under another type matches
              nothing, and a filter that silently returns nothing is worse than
              one that resets. */}
          {isProductMode ? (
            <>
              <div className="sp-row">
                <label className="sp-field">
                  <span className="sp-label">{t('prod_type')}</span>
                  <select
                    className="sp-select"
                    value={pType}
                    onChange={e => {
                      const next = new URLSearchParams(params);
                      if (e.target.value) next.set('ptype', e.target.value);
                      else next.delete('ptype');
                      next.delete('sub');
                      // AND THE PAGE. This one control builds its own params
                      // rather than going through setParam — it has to clear
                      // the subtype at the same time — so it has to repeat
                      // setParam's rule too, or picking a type from page 3
                      // lands on page 3 of a shorter list.
                      next.delete('p');
                      setParams(next, { replace: true });
                      setLoading(true);   // the type is a new query, not a filter
                    }}
                  >
                    <option value="">{t('search_any')}</option>
                    {PRODUCT_TYPES.map(k => (
                      <option key={k} value={k}>{t(`shop_cat_${k}`)}</option>
                    ))}
                  </select>
                </label>

                <label className="sp-field">
                  <span className="sp-label">{t('prod_subtype')}</span>
                  <select
                    className="sp-select"
                    value={pSubtype}
                    onChange={e => setParam('sub', e.target.value)}
                    disabled={!pType}
                  >
                    <option value="">{pType ? t('search_any') : '—'}</option>
                    {subtypesOf(pType).map(k => (
                      <option key={k} value={k}>{t(`prod_sub_${k}`)}</option>
                    ))}
                  </select>
                </label>

                {/* A range, not a maximum: "under 50000" and "between 20 and 40
                    thousand" are both things people look for, and one number
                    can only answer the first. */}
                <label className="sp-field sp-field--price">
                  <span className="sp-label">{t('prod_price_range')}</span>
                  <span className="sp-price-row">
                    <input
                      className="sp-select sp-price-input"
                      type="number" inputMode="numeric" min="0"
                      value={minPrice}
                      onChange={e => setParam('min', e.target.value)}
                      placeholder={t('prod_price_min')}
                    />
                    <span className="sp-price-dash" aria-hidden="true">–</span>
                    <input
                      className="sp-select sp-price-input"
                      type="number" inputMode="numeric" min="0"
                      value={maxPrice}
                      onChange={e => setParam('max', e.target.value)}
                      placeholder={t('prod_price_max')}
                    />
                  </span>
                </label>

              </div>

              {/* Swatches, as in the publish dialog — colour is recognised
                  faster than it is read. "Any" is a swatch of its own so the
                  row is one control rather than a row plus a reset link. */}
              <div className="sp-colors" role="radiogroup" aria-label={t('prod_color')}>
                {/* "ANY COLOUR" — a circle like the swatches beside it, not a
                    pill. As a word it was twice their width and broke the row
                    into a label followed by a palette; as a circle it reads as
                    one of the options, which is what it is.

                    An ✕ rather than a colour, because it is the absence of one.
                    The name moves to the accessible label, where it still
                    reads to a screen reader and on hover. */}
                <button
                  type="button"
                  role="radio"
                  aria-checked={!pColor}
                  className={`sp-color sp-color--any${!pColor ? ' sp-color--on' : ''}`}
                  onClick={() => setParam('color', '')}
                  aria-label={t('search_any')}
                  title={t('search_any')}
                >
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none"
                    stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"
                    aria-hidden="true">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
                {PRODUCT_COLORS.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    role="radio"
                    aria-checked={pColor === c.id}
                    className={`sp-color${pColor === c.id ? ' sp-color--on' : ''}${c.hex ? '' : ' sp-color--multi'}`}
                    style={c.hex ? { '--sw': c.hex } : undefined}
                    onClick={() => setParam('color', pColor === c.id ? '' : c.id)}
                    aria-label={t(`prod_color_${c.id}`)}
                    title={t(`prod_color_${c.id}`)}
                  />
                ))}
              </div>
            </>
          ) : (
          <>
          <div className="sp-row">
            {/* The one filter that differs between the account modes.
                Experience is a minimum on a number; delivery is a yes/no. */}
            {isShopMode ? (
              <label className="sp-field sp-field--check">
                <span className="sp-label">{t('shop_delivery')}</span>
                <span className="sp-check">
                  <input
                    type="checkbox"
                    checked={delivery}
                    onChange={e => setParam('delivery', e.target.checked ? '1' : '')}
                  />
                  <span>{t('search_delivery_only')}</span>
                </span>
              </label>
            ) : (
              <label className="sp-field">
                <span className="sp-label">{t('ds_experience')}</span>
                <select
                  className="sp-select"
                  value={minExperience}
                  onChange={e => setParam('exp', e.target.value)}
                >
                  <option value="">{t('search_any')}</option>
                  {EXPERIENCE_STEPS.map(n => (
                    <option key={n} value={n}>{t('search_exp_min').replace('{n}', n)}</option>
                  ))}
                </select>
              </label>
            )}

            <label className="sp-field">
              <span className="sp-label">{t('ds_location')}</span>
              <select
                className="sp-select"
                value={location}
                onChange={e => setParam('loc', e.target.value)}
              >
                <option value="">{t('search_any')}</option>
                {locations.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </label>

          </div>

          {/* Collapsed by default: a dozen-plus chips wrap to four rows and
              push the results themselves below the fold, on a filter most
              people never touch. Opens automatically when some are already
              selected, so a linked search never hides its own active
              filters. Styles for a designer, categories for a shop — the same
              control, because it does the same job. */}
          <div className="sp-styles-block">
            <button
              type="button"
              className={`sp-styles-toggle${stylesOpen ? ' sp-styles-toggle--open' : ''}`}
              onClick={() => setStylesOpen(o => !o)}
              aria-expanded={stylesOpen}
            >
              <span>{isShopMode ? t('shop_categories') : t('ds_styles')}</span>
              {chips.length > 0 && <span className="sp-styles-n">{chips.length}</span>}
              <svg className="sp-styles-caret" width="14" height="14" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2.4"
                strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {stylesOpen && (
              <div className="sp-styles">
                {(isShopMode ? SHOP_CATEGORIES : DESIGNER_STYLES).map(key => (
                  <button
                    key={key}
                    type="button"
                    className={`sp-chip${chips.includes(key) ? ' sp-chip--on' : ''}`}
                    onClick={() => toggleChip(key)}
                    aria-pressed={chips.includes(key)}
                  >{chipLabel(key)}</button>
                ))}
              </div>
            )}
          </div>
          </>
          )}

          {/* ── CLEAR, LAST ─────────────────────────────────────────────────
              One button for every mode, at the END of the controls card.

              It used to live inside the filter ROW, right-aligned. On a desktop
              that reads fine, but the row wraps to a column on a phone and the
              button landed in the MIDDLE of the filters — above the colour
              swatches in product mode, above the style chips in the other two.
              A reset belongs after the things it resets.

              It clears the FILTERS, not the mode: dropping every parameter
              would send you back to the designer directory, which is not what
              "clear" offers to do. */}
          {hasFilters && (
            <div className="sp-clear-row">
              <button
                type="button"
                className="sp-clear"
                onClick={() => setParams(
                  isProductMode ? { type: MODE_PRODUCT }
                    : isShopMode ? { type: USER_TYPES.SHOP }
                      : {},
                  { replace: true },
                )}
              >{t('search_clear')}</button>
            </div>
          )}
        </div>

        {/* ── results ── */}
        {loading ? (
          <PageSpinner />
        ) : error ? (
          <p className="sp-empty">{error}</p>
        ) : results.length === 0 ? (
          <p className="sp-empty">
            {isProductMode ? t('search_none_products')
              : isShopMode ? t('search_none_shops')
                : t('search_none')}
          </p>
        ) : isProductMode ? (
          <>
            {/* COUNT AND SORT ON ONE LINE, as in the landing catalogue. The
                sort belongs beside the count rather than up in the filter
                panel: it does not narrow anything, it reorders what the
                filters already chose. */}
            <div className="sp-count-row">
              {/* `search_count` reads "{n} designers" — it was written for the
                  directory and is used for all three modes, so this tab has
                  been counting products in designers. The landing catalogue
                  already has the right sentence for a list of listings, and
                  reusing it is also what makes the two pages say the same
                  thing about the same result set. */}
              <p className="sp-count">{t('lnd_cat_found').replace('{n}', results.length)}</p>
              <select
                className="sp-select sp-sort"
                value={pSort}
                /* The DEFAULT is the one that drops out of the URL, and the
                   default is now 'mix' — leaving 'new' here would have written
                   ?sort= for mixed and pinned newest into the link instead. */
                onChange={e => setParam('sort', e.target.value === 'mix' ? '' : e.target.value)}
                aria-label={t('lnd_sort')}
              >
                {/* Mixed is the default, so it is first and it is what an
                    untouched `sort` parameter means. */}
                <option value="mix">{t('lnd_sort_mix')}</option>
                <option value="new">{t('lnd_sort_new')}</option>
                <option value="cheap">{t('lnd_sort_cheap')}</option>
                <option value="dear">{t('lnd_sort_dear')}</option>
              </select>
            </div>
            {/* THE SAME TILES THE SIGNED-OUT CATALOGUE SHOWS, and the same
                pager — see ProductGrid. A listing found while signed in used
                to be a different-looking tile that sprang a lightbox over the
                results; now it is a link to the product's own page, which is
                what makes it openable in a new tab and pasteable to someone. */}
            {/* ── RECOMMENDED ──
                Rendered only when there is something in it. A heading over an
                empty grid, or a section that appears and vanishes as you
                narrow a search, is worse than no section: it reads as the page
                losing results rather than as the tier having none to show. */}
            {recShown.length > 0 && (
              <section className="sp-rec">
                <h2 className="sp-rec-h">{t('sr_rec_title')}</h2>
                <ProductGrid items={recShown} ownerOf={ownerOf} onNavigate={rememberScroll} />
                {recommended.length > REC_MAX && (
                  <button
                    type="button"
                    className="sp-rec-more"
                    onClick={() => setRecOpen(o => !o)}
                    /* The grid it controls, named for a screen reader — the
                       button sits after it, so the relationship is not
                       something the reading order can convey. */
                    aria-expanded={recOpen}
                  >
                    {recOpen
                      ? t('sr_rec_less')
                      : t('sr_rec_more').replace('{n}', recommended.length - REC_MAX)}
                  </button>
                )}
              </section>
            )}

            {/* ── EVERYTHING ──
                Still every listing that matched, paid and free alike, in the
                mixed order. The heading only earns its place once there is a
                section above it to be distinguished from. */}
            <div ref={gridRef}>
              {recShown.length > 0 && <h2 className="sp-rec-h sp-rec-h--all">{t('sr_all_title')}</h2>}
              <ProductGrid items={visible} ownerOf={ownerOf} onNavigate={rememberScroll} />
              <ProductPager page={at} pageCount={pageCount} onPage={goPage} />
            </div>
          </>
        ) : (
          <>
            <p className="sp-count">{t('search_count').replace('{n}', results.length)}</p>
            <div className="sp-grid">
              {/* ONE CARD FOR BOTH. The shape is identical — picture, name, a
                  facts line, up to three chips — and only what fills each slot
                  differs, so the two are normalised here rather than in two
                  copies of the markup. */}
              {results.map(d => {
                const id = d.uid || d.id;
                const chipKeys = (isShopMode ? d.categories : d.styles) || [];
                const meta = isShopMode
                  ? [d.location, d.delivery ? t('shop_delivery') : null]
                  : [
                    d.location,
                    d.experienceYears != null && d.experienceYears !== ''
                      ? t('ds_years_short').replace('{n}', d.experienceYears) : null,
                  ];
                return (
                  <Link
                    key={d.id}
                    to={`${isShopMode ? '/shop' : '/designer'}/${id}`}
                    /* Both roles wear their tier now — read from whichever
                       ladder applies, so a silver SHOP gets the silver card and
                       is never measured against the designer table. */
                    className={`sp-card sp-card--${(isShopMode ? planOfShop : planOf)(d).id}`}
                  >
                    <div className="sp-avatar">
                      {d.avatarUrl ? <img src={d.avatarUrl} alt="" /> : <PersonIcon />}
                    </div>
                    <div className="sp-card-body">
                      <span className="sp-name">{d.fullName || d.name}</span>
                      <span className="sp-meta">{meta.filter(Boolean).join(' · ')}</span>
                      {chipKeys.length > 0 && (
                        <span className="sp-card-styles">
                          {/* Three is what fits one line at the card's width;
                              the rest are on the profile. */}
                          {chipKeys.slice(0, 3).map(s => (
                            <span key={s} className="sp-card-chip">{chipLabel(s)}</span>
                          ))}
                          {chipKeys.length > 3 && (
                            <span className="sp-card-more">+{chipKeys.length - 3}</span>
                          )}
                        </span>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </main>
      {/* No lightbox here any more. A product opens its own PAGE — the full
          picture, the price, the basket, Call/Message and the thread all live
          there, and closing it comes back to these results rather than
          stranding the reader on the shop that sells it. See ProductPage. */}
    </div>
  );
}

export { LIMITS };
